import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { isEqual, cloneDeep, uniqWith } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { FilterPanelItem, FilterPanelOperator, SavedFilter } from './filter-panel.model';
import { IKuiSelectOption } from 'app/key-ui';
import { UserResponse } from '@key-telematics/fleet-api-client';
import { AppService } from 'app/app.service';
import { CurrentClient } from 'app/shared/model';

export interface FilterPanelFilter {
    date?: { start: string, end: string };
    page?: number;
    limit?: number;
    filters?: {
        [key: string]: FilterPanelItem[]
    };
    operators?: {
        [key: string]: FilterPanelOperator
    }
}

@Injectable()
export class FilterPanelService {

    filters: FilterPanelFilter = {
        filters: {},
    };

    private filtersSubject: BehaviorSubject<FilterPanelFilter> = new BehaviorSubject(this.filters);
    private filtersSignal: WritableSignal<FilterPanelFilter> = signal(this.filters);
    get filters$(): Observable<FilterPanelFilter> {
        return this.filtersSubject.asObservable();
    }

    private totalSubject: BehaviorSubject<number> = new BehaviorSubject(0);
    get total$(): Observable<number> {
        return this.totalSubject.asObservable();
    }

    // 'savedFiltersLoaded' is for anayltics purposes
    private savedFiltersLoaded: WritableSignal<boolean> = signal(false);
    readonly savedFiltersLoadedResult: Signal<boolean> = this.savedFiltersLoaded.asReadonly();

    // full saved filters details
    private savedFiltersState: WritableSignal<{ filters: SavedFilter[], selectedId: string | null }> = signal({ filters: [], selectedId: null });

    // computed values have 'result' on the end helps to distinguish them
    allSavedFiltersResult: Signal<SavedFilter[]> = computed(() => this.savedFiltersState().filters);
    selectedSavedFilterIdResult: Signal<string | null> = computed(() => this.savedFiltersState().selectedId);
    selectedSavedFilterResult: Signal<SavedFilter> = computed(() => this.savedFiltersState().filters.find(f => f.id === this.selectedSavedFilterIdResult()));

    hasActiveFiltersResult: Signal<boolean> = computed(() => Object.values(this.filtersSignal().filters || {}).some(v => v.length > 0));

    savedFiltersDropdownListResult: Signal<IKuiSelectOption[]> = computed(() => {
        const { filters, selectedId } = this.savedFiltersState();
        const allOption: IKuiSelectOption = { id: undefined, value: 'All', selected: !selectedId };
        return [allOption, ...filters.map(f => ({ id: f.id, value: f.name, icon: this.getIcon(f.iconIdentifier), selected: f.id === selectedId }))];
    });

    clientFilters: WritableSignal<{ filters: SavedFilter[], selectedId: string | null }> = signal({ filters: [], selectedId: null }); 

    private readonly app = inject(AppService);

    user: WritableSignal<UserResponse> = signal(undefined);

    // for analytics purposes
    setSavedFiltersLoaded(value: boolean) {
        if (this.filters.filters && Object.keys(this.filters.filters).length > 0) {
		    this.savedFiltersLoaded.set(value);
        }
	}

    private emit(filters: FilterPanelFilter, forceEmit = false) {
        if (forceEmit || !isEqual(filters, this.filters)) {
            this.filters = filters;
            this.filtersSignal.set(filters);
            this.filtersSubject.next(cloneDeep(this.filters));
        }
    }

    getUserSavedFilters(user: UserResponse) {
        this.user.set(user);
        const userFlags: string = this.user()?.flags?.['fleet-ui-v2']?.['page-overview-filters'];
        const rawFilters = userFlags ? userFlags.split(';') : null;

        if (rawFilters?.length) {
            const filters = rawFilters.map((f, i) => {
                const [name, rest] = f.split(':eq(');
                const parts = rest.replace(')', '').split(',');
                const filtersMap: { [key: string]: string[] } = {};
                let currentKey: string;
                parts.forEach(p => {
                    if (!p.includes('-')) {
                        currentKey = p;
                        filtersMap[currentKey] = [];
                    } else {
                        filtersMap[currentKey].push(p);
                    }
                });
                return { id: String(i), name, filters: filtersMap, canEdit: true  } as SavedFilter;
            });
            this.updateSavedFilters(filters);
            this.setSavedFiltersLoaded(true);
        }
    }

    getClientFilters(client: CurrentClient) {
        const clientFlags: string = client?.flags?.['fleet-ui-v2']?.['page-overview-filters'];
        if (!clientFlags) { return; }

        const rawFilters: [string, string, string][] = JSON.parse(clientFlags);
        const filters = rawFilters.map(([name, iconIdentifier, filterString], i) => {
            const inner = filterString.slice(filterString.indexOf('(') + 1, filterString.lastIndexOf(')'));
            const [filterKey, ...ids] = inner.split(',');
            return { id: `client_${i}`, name, iconIdentifier, filters: { [filterKey]: ids }, canEdit: false } as SavedFilter;
        });
        this.updateClientSavedFilters(filters);
    }

    // yes this is gross
    getIcon(iconIdentifier: string) {
        let options = {
            fill: '#333333',
            secondaryColorFill: '#ffffff',
            strokeFill: '#ffffff',
            size: 22,
            rotation: 0,
            dropShadowAlpha: 0
        };

        switch (iconIdentifier?.toLowerCase()) {
            case 'camera':
                options.size = 22;
                /* tslint:disable */
                return `<svg width="${options.size}" height="${options.size}" viewBox="0 -2 27 27" version="1.1" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="10" cy="10" r="10" fill="#00A7E1"/>
                            <mask id="path-2-outside-1_1692_4654" maskUnits="userSpaceOnUse" x="4" y="5" width="12" height="9" fill="black">
                                <rect fill="white" x="4" y="5" width="12" height="9"/>
                                <path d="M10.8682 6C11.313 6 11.6738 6.36085 11.6738 6.80566V12.0322C11.6738 12.477 11.313 12.8379 10.8682 12.8379H5.80566C5.36085 12.8379 5 12.477 5 12.0322V6.80566C5 6.36085 5.36085 6 5.80566 6H10.8682ZM14.3926 6.36523C14.661 6.20768 15 6.40161 15 6.71289V12.125C15 12.4363 14.661 12.6302 14.3926 12.4727L12.4541 11.334C12.331 11.2617 12.255 11.13 12.2549 10.9873V7.85059C12.255 7.70782 12.331 7.57617 12.4541 7.50391L14.3926 6.36523Z"/>
                            </mask>
                            <path d="M14.3926 6.36523L14.0664 5.80954L14.0662 5.80966L14.3926 6.36523ZM14.3926 12.4727L14.0662 13.0282L14.0664 13.0284L14.3926 12.4727ZM12.4541 11.334L12.7804 10.7784L12.7802 10.7783L12.4541 11.334ZM12.2549 10.9873H11.6106L11.6106 10.9879L12.2549 10.9873ZM12.2549 7.85059L11.6106 7.85024V7.85059H12.2549ZM12.4541 7.50391L12.7802 8.0596L12.7804 8.05948L12.4541 7.50391ZM10.8682 6V6.64433C10.9571 6.64433 11.0295 6.7167 11.0295 6.80566H11.6738H12.3182C12.3182 6.00499 11.6688 5.35567 10.8682 5.35567V6ZM11.6738 6.80566H11.0295V12.0322H11.6738H12.3182V6.80566H11.6738ZM11.6738 12.0322H11.0295C11.0295 12.1212 10.9571 12.1936 10.8682 12.1936V12.8379V13.4822C11.6688 13.4822 12.3182 12.8329 12.3182 12.0322H11.6738ZM10.8682 12.8379V12.1936H5.80566V12.8379V13.4822H10.8682V12.8379ZM5.80566 12.8379V12.1936C5.7167 12.1936 5.64433 12.1212 5.64433 12.0322H5H4.35567C4.35567 12.8329 5.00499 13.4822 5.80566 13.4822V12.8379ZM5 12.0322H5.64433V6.80566H5H4.35567V12.0322H5ZM5 6.80566H5.64433C5.64433 6.7167 5.7167 6.64433 5.80566 6.64433V6V5.35567C5.00499 5.35567 4.35567 6.00499 4.35567 6.80566H5ZM5.80566 6V6.64433H10.8682V6V5.35567H5.80566V6ZM14.3926 6.36523L14.7187 6.92093C14.558 7.01524 14.3557 6.90009 14.3557 6.71289H15H15.6443C15.6443 5.90313 14.7641 5.40011 14.0664 5.80954L14.3926 6.36523ZM15 6.71289H14.3557V12.125H15H15.6443V6.71289H15ZM15 12.125H14.3557C14.3557 11.9378 14.558 11.8226 14.7187 11.917L14.3926 12.4727L14.0664 13.0284C14.7641 13.4378 15.6443 12.9348 15.6443 12.125H15ZM14.3926 12.4727L14.7189 11.9171L12.7804 10.7784L12.4541 11.334L12.1278 11.8896L14.0662 13.0282L14.3926 12.4727ZM12.4541 11.334L12.7802 10.7783C12.8528 10.8209 12.8991 10.8995 12.8992 10.9867L12.2549 10.9873L11.6106 10.9879C11.6109 11.3605 11.8093 11.7026 12.128 11.8897L12.4541 11.334ZM12.2549 10.9873H12.8992V7.85059H12.2549H11.6106V10.9873H12.2549ZM12.2549 7.85059L12.8992 7.85093C12.8992 7.9384 12.8526 8.01716 12.7802 8.0596L12.4541 7.50391L12.128 6.94821C11.8094 7.13519 11.6108 7.47724 11.6106 7.85024L12.2549 7.85059ZM12.4541 7.50391L12.7804 8.05948L14.7189 6.9208L14.3926 6.36523L14.0662 5.80966L12.1278 6.94834L12.4541 7.50391Z" fill="white" mask="url(#path-2-outside-1_1692_4654)"/>
                        </svg>`;
                /* tslint:enable */

            case 'vehicle tracker':
                options.rotation = 25;
                /* tslint:disable */
                return `<svg width="${options.size}" height="${options.size}" viewBox="4 0 25 25" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                        <title>pin / trip-history / moving</title>
                        <g id="pin-/-trip-history-/-moving" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"
                            transform="rotate(${options.rotation},12.5,12.5)"
                        >
                            <circle id="Oval" fill="#00BE73" cx="12.5" cy="12.5" r="8"></circle>
                            <polygon id="Triangle" fill="#FFFFFF" fill-rule="nonzero" transform="translate(12.500000, 12.00000) scale(0.6) translate(-12.500000, -11.000000) " points="12.5 4 20 18 12.4338882 13.0987632 5 18"></polygon>
                        </g>
                    </svg>`;
                /* tslint:enable */
            
            case 'asset':
                /* tslint:disable */
                return `<svg width="${options.size}" height="${options.size}" viewBox="4 0 25 25" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                        <title>pin / trip-history / non-ignition</title>
                        <g id="pin-/-trip-history-/-non-ignition" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                            <circle cx="12.5" cy="12.5" r="8" fill="#161616"/>
                            <circle cx="12.5" cy="12.5" r="4" fill="#FFFFFF"/>
                        </g>
                    </svg>`;
            /* tslint:enable */

            case 'gemini':
                /* tslint:disable */
                return `<svg width="${options.size}" height="${options.size}" viewBox="-1 -8 40 40" version="1.1" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_1699_4964)">
                            <path d="M0 3.6C0 1.61178 1.61178 0 3.6 0H20.4C22.3882 0 24 1.61178 24 3.6V20.4C24 22.3882 22.3882 24 20.4 24H3.6C1.61178 24 0 22.3882 0 20.4V3.6Z" fill="#FC008E" />
                            <path d="M9.05318 6.59961L6.57163 6.60213C6.08333 6.60263 5.82082 7.17273 6.13948 7.54062L11.9814 14.2851L14.5083 17.2023C14.6168 17.3276 14.7748 17.3996 14.9411 17.3996H17.4278C17.9164 17.3996 18.1794 16.8291 17.8604 16.461L9.48643 6.79682C9.3778 6.67145 9.21959 6.59944 9.05318 6.59961Z" fill="white" />
                            <path d="M17.8605 7.54062C18.1792 7.17273 17.9167 6.60263 17.4284 6.60213L14.9468 6.59961C14.7804 6.59944 14.6222 6.67146 14.5136 6.79682L10 12.0058L12.0186 14.2851L17.8605 7.54062Z" fill="white" />
                            <path d="M11 15.4613L8.99 13.1719L6.13961 16.4611C5.82067 16.8292 6.08365 17.3997 6.57228 17.3997H9.05897C9.22522 17.3997 9.38321 17.3276 9.49171 17.2024L11 15.4613Z" fill="white" />
                        </g>
                        <defs>
                            <clipPath id="clip0_1699_4964">
                                <rect width="24" height="24" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>`;

            default: return null;
        }
    }

    updateClientSavedFilters(filters: SavedFilter[]) {
        this.clientFilters.set({ filters, selectedId: null });
        const userFilters = this.savedFiltersState().filters.filter(f => !this.clientFilters().filters.some(cf => cf.id === f.id));
        this.savedFiltersState.update(s => ({ ...s, filters: [...filters, ...userFilters] }));
    }

    
    updateSavedFilters(filters: SavedFilter[], isNewFilter?: boolean) {
        const selectedId = filters.length ? (isNewFilter ? filters[filters.length - 1].id : this.selectedSavedFilterIdResult()) : undefined;
        const clientFilters = this.clientFilters().filters;
        this.savedFiltersState.update(s => ({ ...s, selectedId, filters: [...clientFilters, ...filters] }));
    }

    setSelectedSavedFilterId(id: string | undefined) {
        this.savedFiltersState.update(s => ({ ...s, selectedId: id }));
    }

    async saveFilter(name: string, isNewFilter: boolean): Promise<UserResponse> {
        const clientFilterIds = new Set(this.clientFilters().filters.map(f => f.id));
        const currentSavedFilters = this.allSavedFiltersResult().filter(f => !clientFilterIds.has(f.id));
        const newId = isNewFilter ? String(currentSavedFilters.length) : undefined;
        const filterBeingSaved: SavedFilter = {
            id: isNewFilter ? newId : this.selectedSavedFilterIdResult(),
            name,
            canEdit: true,
            filters: Object.fromEntries(
                Object.entries(this.filters.filters || {}).map(([key, items]) => [key, items.map(i => i.id)])
            ),
        };

        const updatedSavedFilters = isNewFilter
            ? [...currentSavedFilters, filterBeingSaved]
            : currentSavedFilters.map(f => f.id === filterBeingSaved.id ? filterBeingSaved : f);

        this.updateSavedFilters(updatedSavedFilters, isNewFilter);
        return this.app.api.accounts.updateUser(this.user().id, this.createSavedFilterPayload());
    }

    async deleteSavedFilter(): Promise<UserResponse> {
        const clientFilterIds = new Set(this.clientFilters().filters.map(f => f.id));
        const currentSavedFilters = this.allSavedFiltersResult().filter(f => !clientFilterIds.has(f.id))
        const removedSelectedFilters = currentSavedFilters.filter(f => f.id !== this.selectedSavedFilterIdResult());
        this.updateSavedFilters(removedSelectedFilters);
        this.setSelectedSavedFilterId(undefined);
        return this.app.api.accounts.updateUser(this.user().id, this.createSavedFilterPayload());
    }

    createSavedFilterPayload() {
        const clientFilterIds = new Set(this.clientFilters().filters.map(f => f.id));
        const result = this.savedFiltersState().filters.filter(f => !clientFilterIds.has(f.id)).map(item => {
            const filters = Object.entries(item.filters)
                .filter(([_, values]) => values.length > 0)
                .map(([key, values]) => `${key},${values.join(',')}`)
                .join(',');

            return `${item.name}:eq(${filters})`;
        });

        return {
            flags: {
                ...this.user().flags,
                'fleet-ui-v2': {
                    ...(this.user().flags?.['fleet-ui-v2'] || {}),
                    'page-overview-filters': result.join(';')
                }
            }
        };
    }

    update(values: FilterPanelFilter, forceEmit = false) {
        this.emit({
            ...this.filters,
            ...values,
            page: values.page || 1, // reset the page to 1 if anything other than page is set
        }, forceEmit);
    }

    updateFilterValues(key: string, values: FilterPanelItem[]) {
        this.emit({
            ...this.filters,
            page: 1,
            filters: {
                ...this.filters.filters,
                [key]: uniqWith([...values], isEqual),
            },
        });
    }

    /** this won't replace existing filters, just add to them */
    addFilterValues(key: string, values: FilterPanelItem[]) {
        this.emit({
            ...this.filters,
            page: 1,
            filters: {
                ...this.filters.filters,
                [key]: uniqWith([...this.filters.filters[key] || [], ...values], isEqual),
            },
        });
    }

    resetFilters() {
        this.setSelectedSavedFilterId(null);
        this.emit({
            ...this.filters,
            filters: {},
        });
    }

    updateTotal(total: number) {
        this.totalSubject.next(total);
    }

    updateOperator(key: string, operator: FilterPanelOperator): void {
        this.emit({
            ...this.filters,
            operators: {
                ...this.filters.operators,
                [key]: operator
            }
        });
    }

}
